import type {DefaultError} from "@tanstack/react-query";
import type {UrlQueryKey} from "~/query/queryClient.ts";
import {useCustomQuery} from "~/query/queryClient.ts";
import type {ClContractLifecycle} from "./contractExplorerTypes.ts";

/** `GET /api/data/{databag_id}/contracts/{id}/` — the current version of one contract. */
export const contractDetailUrl = (bagId: number, contractId: number) =>
    `/data/${bagId}/contracts/${contractId}/`;

export type ContractItemApi = {
    id: number;
    item_name: string;
    item_description: string;
    item_quantity: number | null;
    item_unit: string;
    item_unit_price: number | null;
    item_price_currency: string;
    /** ISO code after alias resolution; '' when the document stated none. */
    item_price_currency_clean: string;
    /** Base-currency value — the column contracted_amount is the sum of. */
    item_spend: number | null;
    item_spend_net: number | null;
    item_spend_gross: number | null;
    payment_frequency: string;
    pricing_model: string;
};

export type ContractPaymentTermApi = {
    id: number;
    term_type: string;
    net_days_value: string;
    payment_method: string;
    term_details: string;
    early_payment_discount: string;
    late_payment_interest_penalty: string;
};

export type ContractDetailApi = {
    id: number;
    databag_id: number;
    file_name: string;
    source_relative_path: string;
    contract_title: string;
    contract_type: string;
    contract_type_clean: string;
    contract_type_is_known: boolean;
    contract_status: string;
    lifecycle: ClContractLifecycle;
    supplier_name: string;
    effective_date: string | null;
    expiration_date: string | null;
    contracted_amount: number | null;
    version_no: number;
    /** Version origin: `hermes` (extraction) or `user` (edit). */
    source: string;
    /** Header columns that differ from the extraction (version 1). */
    changed_columns: string[];
    has_review: boolean;
    has_file: boolean;
    parsed_at: string | null;
    as_of: string;
    /** Customer base currency: what contracted_amount and item_spend are in. */
    currency: string;
    /** Distinct currencies the document priced items in; >1 = mixed. */
    contract_currencies: string[];
    items: ContractItemApi[];
    payment_terms: ContractPaymentTermApi[];
};

export function useContractDetailQuery(bagId: number, contractId: number) {
    return useCustomQuery<ContractDetailApi, DefaultError, ContractDetailApi, UrlQueryKey>({
        queryKey: [contractDetailUrl(bagId, contractId)],
        enabled: bagId > 0 && contractId > 0,
    });
}
